import React, { Fragment, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import Dropdown from 'common/Dropdown';
import BackgroundPositionManager from 'common/BackgroundPositionManager';
import I18n from 'modules/i18n';
import useFlags from '@/hooks/useFlags';
import inertPositionAxes from '../../Utils/inertPositionAxes';
import { slidePropTypes } from '../../Utils';
import useSlideShowContext from '../../stores/useSlideShowContext';
import { UPDATE_SLIDE } from '../../Utils/slideShowGraphQL';
import styles from './BackgroundDropdownActions.module.scss';

// Flag-on custom values are stored as a background-position `"X% Y%"` string (0-100 per axis);
// legacy custom is `center N` (vertical only, same 0..100 scale).
const FLAG_ON_CUSTOM_RE = /^(\d+(\.\d+)?% \d+(\.\d+)?%|center \d+)$/;

const ChangeBackgroundPosition = ({ slide }) => {
  const { flushImageTreatment } = useFlags();
  const [updateSlideMutation] = useMutation(UPDATE_SLIDE);
  const locale = useSlideShowContext(state => state.locale);
  const slideRef = useSlideShowContext(state => state.slideRef);
  const updateSlide = useSlideShowContext(state => state.updateSlide);
  const [imageDimensions, setImageDimensions] = useState(null);

  const { imageEsUrl, imageUrl } = slide.imageModel?.image || {};
  const src = locale === 'es' && imageEsUrl && !imageEsUrl.includes('missing.png') ? imageEsUrl : imageUrl;

  useEffect(() => {
    if (!flushImageTreatment || !src) return undefined;

    const img = new Image();
    img.onload = () => setImageDimensions({ height: img.naturalHeight, width: img.naturalWidth });
    img.src = src;

    return () => { img.onload = null; };
  }, [flushImageTreatment, src]);

  const persist = (backgroundPosition) => {
    updateSlideMutation({ variables: { backgroundPosition, id: slide.id } })
      .then((res) => {
        updateSlide(slide.id, res.data.updateSlide.slide);
      })
      // eslint-disable-next-line no-console
      .catch((error) => { console.error(error); });
  };

  const submitHandler = ({ backgroundPosition: bgPosition, customPosition }) => {
    persist(bgPosition === 'custom' ? `center ${customPosition}` : bgPosition);
  };

  const applyPreview = (backgroundPosition) => {
    if (slideRef.current) $(slideRef.current).css({ backgroundPosition });
  };

  const submitCustomPosition = ({ backgroundPosition: bgPosition, customPosition, customPositionY }) => {
    const value = bgPosition === 'custom' ?
      `${customPosition || '50'}% ${customPositionY || '50'}%` :
      bgPosition;

    applyPreview(value);
    persist(value);
  };

  const parseCustom = (value) => {
    // Legacy `center N` rendered as `center N%`, i.e. y = N on the same 0..100 scale.
    if (value.startsWith('center ')) {
      return { customPosition: '50', customPositionY: String(parseInt(value.replace('center ', ''), 10)) };
    }

    const [x, y] = value.split(' ');

    return { customPosition: x.replace('%', ''), customPositionY: y.replace('%', '') };
  };

  const inertAxes = () => {
    const frame = slideRef?.current;

    if (!frame || !imageDimensions) return { x: false, y: false };

    const backgroundSize = slide.imageModel?.styles?.background_size || 'cover';

    return inertPositionAxes({
      fit: backgroundSize === '100% 100%' ? 'fill' : backgroundSize,
      frameHeight: frame.clientHeight,
      frameWidth: frame.clientWidth,
      imageHeight: imageDimensions.height,
      imageWidth: imageDimensions.width,
    });
  };

  const { x: disableHorizontal, y: disableVertical } = inertAxes();

  const options = [
    { label: I18n.t('position_top'), value: 'center top' },
    { label: I18n.t('position_bottom'), value: 'center bottom' },
    { label: I18n.t('position_center'), value: 'center center' },
    { label: I18n.t('position_custom'), value: 'custom' },
  ];

  if (!slideRef) return null;

  return (
    <Dropdown
      buttonClass={`${styles.dropdownItem} ${styles.submenu}`}
      dropdownPosition="right"
      toggleText={(
        <Fragment>
          Change Position
          <i className={`fa fa-angle-right ${styles.angleRight}`} />
        </Fragment>
      )}
    >
      {flushImageTreatment ? (
        <BackgroundPositionManager
          includeVertical
          customMatcher={FLAG_ON_CUSTOM_RE}
          disableHorizontal={disableHorizontal}
          disableVertical={disableVertical}
          horizontalLabel={I18n.t('position_horizontal')}
          idPrefix={`background-position-${slide.id}`}
          initialSliderPosition="50"
          initialSliderPositionY="50"
          livePreview={values => applyPreview(`${values.customPosition}% ${values.customPositionY}%`)}
          object={slide}
          options={options}
          parseCustom={parseCustom}
          submitHandler={submitCustomPosition}
          verticalLabel={I18n.t('position_vertical')}
        />
      ) : (
        <BackgroundPositionManager
          object={slide}
          objectRef={slideRef.slideRef}
          submitHandler={submitHandler}
        />
      )}
    </Dropdown>
  );
};

ChangeBackgroundPosition.propTypes = {
  slide: slidePropTypes.isRequired,
};

export default ChangeBackgroundPosition;
